import React, { useState, useRef, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { Button, Container, Row } from 'reactstrap';
import { refreshCatalog } from './form/shared/CatalogAutocomplete';
import config from '../../../config';
import TableBuilder from '../../../shared/components/table/TableBuilder';
import { getExportPrefix } from '../../../shared/components/table/functions';
import CardPanel from '../../../shared/components/widgets/CardPanel';
import { doFetch } from '../../../helpers';

// Meta Product Sets panel for the BGD Items tab. Lists Meta catalog product
// sets (POST /meta/product-sets/list) below the main catalog panel, using
// its own server-side AG Grid datasource with cursor-based pagination
// (matching the pattern used for Meta's item catalog).
function MetaProductSetsTable() {
  const selectedSeller = useSelector(store => store.sellerSelect);
  const user = useSelector(store => store.authentication.user);
  const [agGrid, setAgGrid] = useState(null);

  // Cursor cache for Meta's pagination (offset -> cursor mapping)
  const cursorCacheRef = useRef({ 0: null });

  const pageSize = 100;

  const exportPrefix = getExportPrefix('meta-product-sets', selectedSeller, true);

  const columnDefs = [
    {
      field: 'name',
      headerName: 'Name',
    },
    {
      field: 'id',
      headerName: 'Product Set ID',
    },
    {
      field: 'product_count',
      headerName: 'Product Count',
    },
    {
      field: 'retailer_id',
      headerName: 'Retailer ID',
    },
    {
      field: 'live_metadata.external_url',
      headerName: 'External URL',
      cellRenderer: (params) => {
        const url = params.data?.live_metadata?.external_url;
        if (url) {
          return (
            <a href={url} target="_blank" rel="noopener noreferrer">
              {url}
            </a>
          );
        }
        return '';
      },
    },
    {
      field: 'live_metadata.integrity_review_status',
      headerName: 'Integrity Review Status',
    },
    {
      field: 'filter',
      headerName: 'Filter',
      hide: true,
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: false,
    resizable: true,
  };

  const apiEndPoint = `${config.serverUrl}/meta/product-sets/list`;

  const tableConfig = {
    showDatePicker: false,
    showFooter: false,
    parentControlledLoad: true,
    apiEndPoint,
    paginationPageSize: pageSize,
    cacheBlockSize: pageSize,
  };

  // Helper function to fetch a specific page with cursor
  const fetchProductSetsPage = async (cursor, headerWithAuth, accountId) => {
    const requestOptions = {
      method: 'POST',
      headers: headerWithAuth,
      body: JSON.stringify({
        accountId,
        data: {
          fields: ['id', 'name', 'product_count', 'retailer_id', 'filter', 'live_metadata{external_url,integrity_review_status}'],
          limit: pageSize,
          ...(cursor ? { after: cursor } : {}),
        },
      }),
    };
    return doFetch(apiEndPoint, requestOptions);
  };

  const datasource = {
    getRows: async (params) => {
      const { request, success, fail } = params;
      const { startRow } = request;
      const headerWithAuth = { ...config.jsonHeader, authorization: `JWT ${user.jwtToken}` };
      const accountId = selectedSeller.parentAccountId > 0 ? selectedSeller.parentAccountId : selectedSeller.value;

      const offset = startRow || 0;

      try {
        // Get cursor for this page
        const cursor = cursorCacheRef.current[offset];

        // Only forward navigation is supported (cursor is cached from previous page)
        if (offset > 0 && cursor === undefined) {
          console.warn('Meta: Missing cursor for offset', offset);
          fail();
          return;
        }

        const data = await fetchProductSetsPage(cursor, headerWithAuth, accountId);
        const rows = data.data || [];

        // Meta always returns a paging.cursors object (before/after) even on
        // the last page, so its presence alone doesn't indicate more data
        // exists. The reliable signal is whether a full page was returned -
        // a short page means we've reached the end.
        const hasNextPage = rows.length === pageSize && !!data.paging?.cursors?.after;

        // Cache next cursor only when we know there's actually another page
        if (hasNextPage) {
          cursorCacheRef.current[offset + pageSize] = data.paging.cursors.after;
        }

        // No total count is returned by this endpoint - use a sentinel +1 row
        // count only while we know another page exists, so infinite scroll
        // stops instead of showing a permanent "Loading..." row.
        const rowCount = hasNextPage
          ? offset + rows.length + 1
          : offset + rows.length;

        success({
          rowData: rows,
          rowCount,
        });
      } catch (error) {
        console.error('Meta product sets fetch error:', error);
        fail();
      }
    },
  };

  const parentAccess = (agGridPointer) => {
    setAgGrid(agGridPointer);
  };

  useEffect(() => {
    if (agGrid) {
      cursorCacheRef.current = { 0: null };
      agGrid.refreshData();
    }
  }, [selectedSeller.value, agGrid]);

  return (
    <Container className="todo-app">
      <Row>
        <CardPanel
          title="Meta Product Sets"
          subhead={new Date().toISOString()}
          autorenewButton={false}
          closeButton={false}
        >
          <div style={{ display: 'flex', justifyContent: 'flex-start', marginBottom: '8px' }}>
            <Button
              size="sm"
              color="primary"
              outline
              onClick={() => refreshCatalog({
                platform: 'meta',
                accountId: selectedSeller.parentAccountId > 0 ? selectedSeller.parentAccountId : selectedSeller.value,
                jwtToken: user.jwtToken,
              })}
            >
              Product Sets Re-Sync
            </Button>
          </div>
          <TableBuilder
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            apiEndPoint={tableConfig.apiEndPoint}
            showDatePicker={tableConfig.showDatePicker}
            showFooter={tableConfig.showFooter}
            rowHeight={40}
            pagination
            rowModelType="serverSide"
            datasource={datasource}
            paginationPageSize={tableConfig.paginationPageSize}
            cacheBlockSize={tableConfig.cacheBlockSize}
            serverSideInfiniteScroll={false}
            parentControlledLoad={tableConfig.parentControlledLoad}
            editSelectedRowsButton={false}
            showImportButton={false}
            parentAccess={parentAccess}
            exportPrefix={exportPrefix}
          />
        </CardPanel>
      </Row>
    </Container>
  );
}

export default MetaProductSetsTable;
